/**
 * Schools table → .xlsx export for Product Insights · LAE (Productivity, Schools mode).
 *
 * Ports from the LAE app (`src/features/productivity/`):
 *   - `SchoolsView.jsx`: `flattenForExport`, `specialistNames`, `EXPORT_LABELS` and the export
 *     column list built inside the view;
 *   - `exportColumns.js`: `SCHOOL_ASSIGNMENT_EXPORT_KEYS`.
 * `groupSpecialists` is NOT duplicated here: it lives in `./lae-school-rows` (P6).
 *
 * Deliberate differences from LAE (spec §6):
 *   - The writer is SEED EMERGE's `exportRowsToXlsx` (lazy SheetJS, header autofilter), not LAE's
 *     ExportButton. The grand total is a DATA row, because the helper's `totals` option takes
 *     decimal strings.
 *   - Consolidated mode (split off): LAE emitted only group Total rows with empty dates and
 *     specialists. Here assignment lines are emitted only when split is on, and every Total row
 *     takes its dates and specialists from `__allLines ?? assignments`, so both modes carry them.
 *   - A "SEED EMERGE company" column (the mapped company's name, or empty) sits right after School
 *     (owner decision 2026-10-29).
 *   - Duration headers carry " (seconds)" and rate headers " (%)": unit labels, since the cells
 *     hold raw seconds and 0..100 rates.
 *   - Empty text and absent figures are empty cells, never 0.
 */
import { COUNT_NUMBER_FORMAT, exportRowsToXlsx } from '@/lib/xlsx-export';
import type { XlsxExportColumn } from '@/lib/xlsx-export';
import type { LaeProdAssignmentLine } from '@/lib/types';
import { groupSpecialists } from './lae-school-rows';
import { fmtRange } from './lae-format';

/**
 * Schools mode, one sheet row per assignment plus a Total row per school × funnel. The metric
 * columns follow the table's order; Records and Leads worked are separate columns.
 */
export const SCHOOL_ASSIGNMENT_EXPORT_KEYS = [
  'allocated', 'worked',
  'calls', 'connect_rate', 'call_seconds', 'avg_talk_seconds',
  'counselling_completed', 'ccm_talk_seconds', 'avg_ccm_talk_seconds',
  'advanced', 'adv_talk_seconds', 'avg_adv_talk_seconds',
  'regressed', 'neg_talk_seconds', 'avg_neg_talk_seconds',
  'applications',
] as const;

type ExportMetricKey = (typeof SCHOOL_ASSIGNMENT_EXPORT_KEYS)[number];

type ExportMetrics = { [K in ExportMetricKey]?: number | null };

/** Export headers: the metric labels, except `allocated`, which reads as Records next to Leads worked. */
const EXPORT_LABELS: Record<ExportMetricKey, string> = {
  allocated: 'Records',
  worked: 'Leads Worked On',
  calls: 'Total Calls Dialled',
  connect_rate: 'Answer Rate',
  call_seconds: 'Total Call Time',
  avg_talk_seconds: 'Avg Call Time',
  counselling_completed: 'Marked as Interested in 2027 Intakes',
  ccm_talk_seconds: 'Total Call Time - Interested in 2027 Intakes',
  avg_ccm_talk_seconds: 'Avg Call Time - Interested in 2027 Intakes',
  advanced: 'Positive Status Changes',
  adv_talk_seconds: 'Total Call Time - Positive Changes',
  avg_adv_talk_seconds: 'Avg Call Time - Positive Changes',
  regressed: 'Negative Status Changes',
  neg_talk_seconds: 'Total Call Time - Negative Changes',
  avg_neg_talk_seconds: 'Avg Call Time - Negative Changes',
  applications: 'Applications',
};

const RATE_KEYS: ReadonlySet<ExportMetricKey> = new Set<ExportMetricKey>(['connect_rate']);
const DURATION_KEYS: ReadonlySet<ExportMetricKey> = new Set<ExportMetricKey>([
  'call_seconds', 'avg_talk_seconds',
  'ccm_talk_seconds', 'avg_ccm_talk_seconds',
  'adv_talk_seconds', 'avg_adv_talk_seconds',
  'neg_talk_seconds', 'avg_neg_talk_seconds',
]);

const RATE_NUMBER_FORMAT = '0.0';

/**
 * A table row as P6 `schoolRowsOf` builds it (the group subtotal spread in, plus identity and lines).
 * Structural on purpose, so the table's own row type is assignable without an import cycle.
 */
interface ExportSourceRow extends ExportMetrics {
  school_name: string | null;
  funnel?: string | null;
  funnel_label?: string | null;
  /** Lines shown in split mode; `[]` when consolidated. */
  assignments?: LaeProdAssignmentLine[];
  /** Every line, kept when consolidated. */
  __allLines?: LaeProdAssignmentLine[];
  specialists_worked?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  /** The mapped SEED EMERGE company; undefined = an older API. */
  emerge_company?: { id: number; name: string } | null;
}

/** One sheet row. Text columns are `''` when empty; figures are null when absent. */
interface ExportSheetRow extends ExportMetrics {
  school_name: string;
  emerge_company: string;
  funnel: string;
  assignment: string;
  cycle: string;
  status: string;
  start_date: string;
  end_date: string;
  specialists: string;
  specialists_worked: number | null;
}

interface SpecialistLike {
  id: string | number;
  name?: string | null;
  worked?: boolean;
}

/** Specialists as "A. Khan*, B. Rao": worked in the period marked with an asterisk. */
function specialistNames(list: readonly SpecialistLike[] | null | undefined): string {
  return (list ?? []).map((p) => `${p.name || `#${p.id}`}${p.worked ? '*' : ''}`).join(', ');
}

function minDate(list: readonly (string | null | undefined)[]): string {
  return list.filter((d): d is string => !!d).sort()[0] ?? '';
}

function maxDate(list: readonly (string | null | undefined)[]): string {
  return list.filter((d): d is string => !!d).sort().at(-1) ?? '';
}

function metricsOf(source: ExportMetrics): ExportMetrics {
  const out: ExportMetrics = {};
  for (const key of SCHOOL_ASSIGNMENT_EXPORT_KEYS) out[key] = source[key] ?? null;
  return out;
}

/**
 * Flattens the table's filtered and sorted school × funnel rows into sheet rows: one per assignment
 * line (split mode only), a Total row per group (the server subtotal, so people are not double
 * counted), then the grand total when the table shows every group. A filter that drops a group
 * would make the person-deduplicated grand total wrong, so it is left out then.
 */
export function flattenForExport(
  rows: readonly ExportSourceRow[],
  totals: ExportMetrics | null | undefined,
  groupCount: number,
  split: boolean,
): ExportSheetRow[] {
  const out: ExportSheetRow[] = [];
  for (const r of rows) {
    const base = {
      school_name: r.school_name ?? '',
      emerge_company: r.emerge_company?.name ?? '',
      funnel: r.funnel_label || r.funnel || '',
    };
    const lines = r.__allLines ?? r.assignments ?? [];
    if (split) {
      for (const a of r.assignments ?? []) {
        out.push({
          ...metricsOf(a),
          ...base,
          assignment: a.name || a.assignment_type || '',
          cycle: a.cycle ?? '',
          status: a.status ?? '',
          start_date: a.start_date ?? '',
          end_date: a.end_date ?? '',
          specialists: specialistNames(a.specialists),
          specialists_worked: (a.specialists ?? []).filter((p) => p.worked).length,
        });
      }
    }
    const people: readonly SpecialistLike[] = groupSpecialists(lines);
    out.push({
      ...metricsOf(r),
      ...base,
      assignment: 'Total',
      cycle: '',
      status: '',
      start_date: minDate(lines.map((a) => a.start_date)) || (r.start_date ?? ''),
      end_date: maxDate(lines.map((a) => a.end_date)) || (r.end_date ?? ''),
      specialists: specialistNames(people),
      specialists_worked:
        r.specialists_worked ?? (lines.length > 0 ? people.filter((p) => p.worked).length : null),
    });
  }
  if (out.length > 0 && totals && rows.length === groupCount) {
    out.push({
      ...metricsOf(totals),
      school_name: 'Total',
      emerge_company: '',
      funnel: '',
      assignment: '',
      cycle: '',
      status: '',
      start_date: '',
      end_date: '',
      specialists: '',
      specialists_worked: null,
    });
  }
  return out;
}

const textOrNull = (v: string): string | null => (v === '' ? null : v);

const TEXT_COLUMNS: readonly [Exclude<keyof ExportSheetRow, ExportMetricKey | 'specialists_worked'>, string][] = [
  ['school_name', 'School'],
  ['emerge_company', 'SEED EMERGE company'],
  ['funnel', 'Funnel'],
  ['assignment', 'Assignment'],
  ['cycle', 'Cycle'],
  ['status', 'Status'],
  ['start_date', 'Start Date'],
  ['end_date', 'End Date'],
  ['specialists', 'Specialists'],
];

function headerOf(key: ExportMetricKey): string {
  const label = EXPORT_LABELS[key];
  if (DURATION_KEYS.has(key)) return `${label} (seconds)`;
  if (RATE_KEYS.has(key)) return `${label} (%)`;
  return label;
}

const EXPORT_COLUMNS: readonly XlsxExportColumn<ExportSheetRow>[] = [
  ...TEXT_COLUMNS.map(([key, label]) => ({
    key,
    label,
    value: (r: ExportSheetRow) => textOrNull(r[key]),
  })),
  {
    key: 'specialists_worked',
    label: 'Specialists Worked',
    value: (r: ExportSheetRow) => r.specialists_worked,
    numFmt: COUNT_NUMBER_FORMAT,
  },
  ...SCHOOL_ASSIGNMENT_EXPORT_KEYS.map((key) => ({
    key,
    label: headerOf(key),
    value: (r: ExportSheetRow) => r[key] ?? null,
    numFmt: RATE_KEYS.has(key) ? RATE_NUMBER_FORMAT : COUNT_NUMBER_FORMAT,
  })),
];

const SOURCE_LABELS: Record<string, string> = { system: 'System', manual: 'Manual' };

export interface ExportSchoolsXlsxOptions {
  rows: readonly ExportSourceRow[];
  totals: ExportMetrics | null | undefined;
  groupCount: number;
  split: boolean;
  from: string;
  to: string;
  /** LAE cycle label (`2026-27`); empty, undefined or `all` = every cycle. */
  cycle?: string | null;
  funnelLabel?: string | null;
  /** `system` | `manual`; anything else = all calls. */
  source?: string | null;
}

/** Writes the Schools sheet and triggers the download. Rejects if SheetJS fails to load. */
export async function exportSchoolsXlsx({
  rows,
  totals,
  groupCount,
  split,
  from,
  to,
  cycle,
  funnelLabel,
  source,
}: ExportSchoolsXlsxOptions): Promise<void> {
  const cycleText = cycle && cycle !== 'all' ? cycle : 'All cycles';
  const sourceText = (source && SOURCE_LABELS[source]) || 'All calls';
  await exportRowsToXlsx<ExportSheetRow>({
    fileName: `lae-productivity-schools-${from}_${to}.xlsx`,
    sheetName: 'Schools',
    title: [
      'Lead & Application Engagement · Schools',
      `${fmtRange(from, to)} · India time`,
      `Cycle: ${cycleText}`,
      `Funnel: ${funnelLabel || 'All funnels'}`,
      `Calls: ${sourceText}`,
      split ? 'Split by assignment' : 'One line per school',
    ],
    columns: EXPORT_COLUMNS,
    rows: flattenForExport(rows, totals, groupCount, split),
  });
}
